import * as Styled from "./DataTable.styles";

import { CloseOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { Divider, InputRef } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";

import DataTableSkeleton from "./DataTableSkeleton";
import Error from "components/Error/Error";
import NoData from "./NoData/NoData";
import Switches from "components/Switches/Switches";
import Tabs from "components/Tabs/Tabs";
import TopControls from "components/TopControls/TopControls";
import { useLocation } from "context/LocationContext";

function DataTable() {
  const [editPartnerVenueId, setEditPartnerVenueId] = useState<boolean>(false);
  const [newPartnerVenueId, setNewPartnerVenueId] = useState<
      string | undefined
  >(undefined);


  const [editLocationName, setEditLocationName] = useState<boolean>(false);
  const [newLocationName, setNewLocationName] = useState<
      string | undefined
  >(undefined);


  const {
    locationActions,
    currentLocation,
    locationDetails,
    isLocationDetailsLoading,
    isLocationDetailsError,
    locationDetailsError,
    refetchLocationDetails,
    refetchLocationConfigs,
  } = useLocation();


  const inputRef = useRef<InputRef>(null);

  const handleRetry = useCallback(() => {
    void refetchLocationDetails?.();
    void refetchLocationConfigs?.();
  }, [refetchLocationConfigs, refetchLocationDetails]);


  useEffect(() => {
    setEditLocationName(false);
    setNewLocationName(undefined);

    setNewPartnerVenueId(undefined);
    setEditPartnerVenueId(false);
    inputRef.current?.blur();
  }, [currentLocation?.id]);


  const handleEditPartnerVenueId = useCallback(() => {
    if (editPartnerVenueId && inputRef.current?.input?.value) {
      setNewPartnerVenueId(undefined);

      inputRef.current.input.value = locationDetails?.partnerVenueId ?? "";
    }

    setEditPartnerVenueId(!editPartnerVenueId);
  }, [locationDetails?.partnerVenueId, editPartnerVenueId]);


  const handleEditLocationName = useCallback(() => {
    if (editLocationName && inputRef.current?.input?.value) {
      setNewLocationName(undefined);

      inputRef.current.input.value = locationDetails?.name ?? "";
    }

    setEditLocationName(!editLocationName);
  }, [locationDetails?.name, editLocationName]);


  useEffect(() => {
    if (editPartnerVenueId && inputRef.current) {
      inputRef.current.focus({
        cursor: "end",
      });
    }

    if (editLocationName && inputRef.current) {
      inputRef.current.focus({
        cursor: "end",
      });
    }


  }, [editPartnerVenueId, editLocationName]);

  const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewPartnerVenueId(event.currentTarget.value);
      },
      []
  );

  const handleLocationInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewLocationName(event.currentTarget.value);
      },
      []
  );

  const handleSavePartnerVenueId = useCallback(() => {
    if (!newPartnerVenueId) {
      return console.error("Incomplete params to update the location");
    }

    locationActions.update({
      newPartnerVenueId,
    });
  }, [locationActions, newPartnerVenueId]);

  const handleSavelocationName = useCallback(() => {
    if (!newLocationName) {
      return console.error("Incomplete params to update the location");
    }

    locationActions.update({
      newLocationName,
    });
  }, [locationActions, newLocationName]);




  useEffect(() => {

    if (locationActions.updateSuccess && newPartnerVenueId) {
      setNewPartnerVenueId(undefined);
      setEditPartnerVenueId(false);
      handleRetry();
    }


    if (locationActions.updateSuccess && newLocationName) {
      setNewLocationName(undefined);
      setEditLocationName(false);
      handleRetry();
    }



  }, [handleRetry, locationActions.updateSuccess, newPartnerVenueId, newLocationName]);

  if (isLocationDetailsLoading) {
    return <DataTableSkeleton />;
  }

  if (isLocationDetailsError && locationDetailsError) {
    return (
      <Styled.Container>
        <Styled.ContentContainer>
          <Error
            title="Error fetching the Table Status"
            error={locationDetailsError}
            onRetry={handleRetry}
          />
        </Styled.ContentContainer>
      </Styled.Container>
    );
  }

  if (!locationDetails) {
    return (
      <Styled.Container>
        <Styled.ContentContainer>
          <NoData onRetry={handleRetry} />
        </Styled.ContentContainer>
      </Styled.Container>
    );
  }

  const { name, locationId, partnerVenueId, posType } = locationDetails;

  return (
    <Styled.Container>
      <Styled.ContentContainer>
        <Styled.InfoMessage
          className={!currentLocation?.enabled ? "show" : ""}
          type="info"
          message="Location Disabled."
          description="This location is currently disabled and the functionality is limited. To enable all functionalities, please enable this location."
          showIcon
        />
        <Styled.Header
          key={locationId}
          title={
            !editLocationName ? (
              <>
                <span>{name} </span>
                <Styled.EditButton
                  onClick={handleEditLocationName}
                  disabled={locationActions.isUpdateLoading}
                  data-testid="edit-location-name"
                >
                  <EditOutlined/>
                </Styled.EditButton></>
            ) : (
              <Styled.ItemContainer>
                <Styled.Input
                  key={`${locationId}_${name}_location`}
                  disabled={!editLocationName}
                  defaultValue={name?? ""}
                  value={newLocationName ?? name??""}
                  onChange={(event) => handleLocationInputChange(event)}
                  ref={inputRef}
                  style={{ width: "auto" }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSavelocationName();
                    }
                  }}
                />
                <Styled.ButtonContainer>
                  {editLocationName && (
                    <Styled.EditButton
                      disabled={!newLocationName || locationActions.isUpdateLoading}
                      loading={locationActions.isUpdateLoading}
                      onClick={handleSavelocationName}
                      type="default"
                      name="Save"
                      data-testid="saveLocationName"

                    >
                      {!locationActions.isUpdateLoading && <SaveOutlined/>}
                    </Styled.EditButton>
                  )}
                  <Styled.EditButton
                    disabled={locationActions.isUpdateLoading}
                    onClick={handleEditLocationName}
                    type="default"
                    name={!editLocationName ? "Edit" : "Close"}

                  >
                    {!editLocationName ? <EditOutlined/> : <CloseOutlined/>}
                  </Styled.EditButton>
                </Styled.ButtonContainer>
              </Styled.ItemContainer> )}

          layout="vertical"
          bordered
          size="small"
          extra={<TopControls />}
          column={4}
        >
          <Styled.HeaderItem label="Last saved:">{currentLocation?.lastUpdate}</Styled.HeaderItem>
          <Styled.HeaderItem label="ID:">{locationId}</Styled.HeaderItem>
          <Styled.HeaderItem label="Pos Type:">{posType}</Styled.HeaderItem>
          <Styled.HeaderItem label="Partner Venue ID:">
            <Styled.ItemContainer>
              <Styled.Input
                key={`${locationId}_${name}_partnerVenueId`}
                disabled={!editPartnerVenueId}
                defaultValue={partnerVenueId}
                value={newPartnerVenueId ?? partnerVenueId}
                onChange={(event) => handleInputChange(event)}
                ref={inputRef}
              />
              <Styled.ButtonContainer>
                {editPartnerVenueId && (
                  <Styled.EditButton
                    disabled={
                      !newPartnerVenueId || locationActions.isUpdateLoading
                    }
                    loading={locationActions.isUpdateLoading}
                    onClick={handleSavePartnerVenueId}
                    type="default"
                    name="Save"
                    data-testid="savePartnerVenueId"
                  >
                    {!locationActions.isUpdateLoading && <SaveOutlined />}
                  </Styled.EditButton>
                )}
                <Styled.EditButton
                  disabled={locationActions.isUpdateLoading}
                  onClick={handleEditPartnerVenueId}
                  type="default"
                  name={!editPartnerVenueId ? "Edit" : "Close"}
                  data-testid="editPartnerVenueId"
                >
                  {!editPartnerVenueId ? <EditOutlined /> : <CloseOutlined />}
                </Styled.EditButton>
              </Styled.ButtonContainer>
            </Styled.ItemContainer>
          </Styled.HeaderItem>
        </Styled.Header>

        <Divider orientation="left">
            Enable / Disable Dashboard Services
        </Divider>
        <Switches />
        <Tabs />
      </Styled.ContentContainer>
    </Styled.Container>
  );
}

export default DataTable;
